import React, { useState, useEffect } from 'react';
import { X, Search, Plus, Trash2, ChevronDown, ChevronRight, User, Gem, Package, FileText } from 'lucide-react';
import { API_URL } from '../../services/api';

const CommandeProductionClient = ({ onClose, onLancerProduction }) => {
  // Pas d'onglets - workflow simplifié
  
  // Destinataire (client ou boutique)
  const [destinataire, setDestinataire] = useState(null); // {type: 'client'|'boutique', id: X, nom: 'Y'}
  const [destinataireSearch, setDestinataireSearch] = useState('');
  
  // Recherche bijou par référence
  const [referenceSearch, setReferenceSearch] = useState('');
  const [bijouTrouve, setBijouTrouve] = useState(null);
  const [quantite, setQuantite] = useState(1);
  
  // Tableau des lignes de commande
  const [lignesCommande, setLignesCommande] = useState([]);
  const [ligneExpanded, setLigneExpanded] = useState(null);
  
  // Données
  const [boutiques, setBoutiques] = useState([]);
  const [clients, setClients] = useState([]);
  const [bijouxDisponibles, setBijouxDisponibles] = useState([]);
  const [devisValides, setDevisValides] = useState([]);
  const [pierresDisponibles, setPierresDisponibles] = useState([]);
  const [filteredDestinataires, setFilteredDestinataires] = useState([]);
  const [filteredBijoux, setFilteredBijoux] = useState([]);
  const [filteredPierres, setFilteredPierres] = useState([]);
  const [showDestinataireSuggestions, setShowDestinataireSuggestions] = useState(false);
  const [showBijouSuggestions, setShowBijouSuggestions] = useState(false);
  
  const [formMoule, setFormMoule] = useState('');

  // Ajout de pierres — indexé par sous-ligne ID
  const [pierreSearch, setPierreSearch] = useState('');
  const [showPierreSuggestions, setShowPierreSuggestions] = useState(false);
  const [activePierreSl, setActivePierreSl] = useState(null); // ID de la sous-ligne active

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [bijouxRes, catalogueRes, boutiquesRes, clientsRes, devisRes, pierresRes] = await Promise.all([
        fetch(`${API_URL}/atelier/fabrication?statut=termine`),
        fetch(`${API_URL}/bijoux/catalogue/modeles`),
        fetch(`${API_URL}/boutiques`),
        fetch(`${API_URL}/clients`),
        fetch(`${API_URL}/devis`),
        fetch(`${API_URL}/stock-matieres/stock`)
      ]);

      const bijoux = await bijouxRes.json();
      const catalogue = await catalogueRes.json();
      const boutiquesData = await boutiquesRes.json();
      const clientsData = await clientsRes.json();
      const devisData = await devisRes.json();
      const pierresData = await pierresRes.json();

      // Fusionner les OF terminés + les modèles du catalogue
      const catalogueModeles = (Array.isArray(catalogue) ? catalogue : []).map(m => ({
        id: m.id || m.reference_article,
        code_article: m.reference_article,
        reference_produit: m.reference_article,
        type_bijou: (m.type_bijou || '').toUpperCase(),
        description: m.nom_modele || m.description || m.reference_article,
        metal: 'JAUNE',
        numero_ordre: null,
        _source: 'catalogue',
        _pieces: m.pieces || [],
        _pvp: m.pvp_magasin || m.prix_vente,
        _collection: m.collection_nom,
      }));

      const ofTermines = (Array.isArray(bijoux) ? bijoux : []).map(b => ({ ...b, _source: 'of' }));

      // Catalogue en premier, puis OFs
      setBijouxDisponibles([...catalogueModeles, ...ofTermines]);
      setBoutiques(boutiquesData || []);
      setClients(clientsData || []);
      
      // Filtrer seulement les pierres du stock
      const pierresFiltrees = (pierresData || []).filter(item => 
        item.type_matiere === 'PIERRE' || 
        item.categorie === 'PIERRE' ||
        item.reference?.toLowerCase().includes('dia') ||
        item.reference?.toLowerCase().includes('sap') ||
        item.reference?.toLowerCase().includes('rub') ||
        item.reference?.toLowerCase().includes('eme')
      );
      setPierresDisponibles(pierresFiltrees);
      
      // Filtrer les devis validés et non en production
      const disponibles = (devisData || []).filter(d => 
        d.statut !== 'EN_PRODUCTION' && 
        d.statut !== 'TERMINE' &&
        d.statut !== 'ANNULE'
      );
      setDevisValides(disponibles);
    } catch (error) {
      console.error('Erreur chargement:', error);
    }
  };

  const handleDestinataireSearch = (value) => {
    setDestinataireSearch(value);
    if (value.trim().length > 0) {
      // Rechercher dans les clients
      const clientsFiltered = clients.map(c => ({
        type: 'client',
        id: c.id,
        nom: `${c.nom} ${c.prenom}`,
        details: c.telephone || '',
        data: c
      })).filter(c => 
        c.nom.toLowerCase().includes(value.toLowerCase()) ||
        c.details.includes(value)
      );

      // Rechercher dans les boutiques
      const boutiquesFiltered = boutiques.map(b => ({
        type: 'boutique',
        id: b.id,
        nom: b.nom,
        details: b.ville || '',
        data: b
      })).filter(b => 
        b.nom.toLowerCase().includes(value.toLowerCase()) ||
        b.details.toLowerCase().includes(value.toLowerCase())
      );

      // Combiner les résultats
      const combined = [...clientsFiltered, ...boutiquesFiltered];
      setFilteredDestinataires(combined);
      setShowDestinataireSuggestions(combined.length > 0);
    } else {
      setFilteredDestinataires([]);
      setShowDestinataireSuggestions(false);
    }
  };

  const selectDestinataire = (dest) => {
    setDestinataire(dest);
    setDestinataireSearch(dest.nom);
    setShowDestinataireSuggestions(false);
  };

  const handleReferenceSearch = (value) => {
    setReferenceSearch(value);
    if (value.trim().length >= 2) {
      const term = value.toLowerCase();
      const filtered = bijouxDisponibles.filter(b =>
        (b.code_article || '').toLowerCase().includes(term) ||
        (b.reference_produit || '').toLowerCase().includes(term) ||
        (b.numero_ordre || '').toLowerCase().includes(term) ||
        (b.type_bijou || '').toLowerCase().includes(term) ||
        (b.description || '').toLowerCase().includes(term) ||
        (b._collection || '').toLowerCase().includes(term)
      );
      // Dédupliquer par code_article (priorité catalogue)
      const seen = new Set();
      const unique = filtered.filter(b => {
        const key = b.code_article || b.id;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setFilteredBijoux(unique.slice(0, 20));
      setShowBijouSuggestions(unique.length > 0);
    } else {
      setFilteredBijoux([]);
      setShowBijouSuggestions(false);
      setBijouTrouve(null);
    }
  };

  const selectBijou = async (bijou) => {
    setBijouTrouve(bijou);
    setReferenceSearch(bijou.code_article || bijou.numero_ordre);
    setShowBijouSuggestions(false);

    try {
      if (bijou._source === 'catalogue') {
        // Charger depuis la fiche complète du premier bijou de cette référence
        const firstPiece = (bijou._pieces || []).find(p => p.id);
        if (firstPiece) {
          const res = await fetch(`${API_URL}/bijoux/${firstPiece.id}/fiche-complete`);
          if (res.ok) {
            const fiche = await res.json();
            const b = fiche.bijou || {};
            setBijouTrouve({
              ...bijou,
              metal: firstPiece.couleur_or || 'JAUNE',
              details: {
                poids_or: b.poids_metal || b.poids_brut || 0,
                pierres: (fiche.pierres_of || []).map(p => ({
                  reference: p.reference_pierre || '',
                  designation: p.type_pierre || 'Pierre',
                  quantite_carats: p.poids_carats || 0,
                  quantite: p.quantite || 1,
                })),
                composants: fiche.composants || [],
              }
            });
          }
        } else {
          setBijouTrouve({ ...bijou, details: { poids_or: 0, pierres: [], composants: [] } });
        }
      } else {
        // Charger depuis l'OF
        const response = await fetch(`${API_URL}/atelier/fabrication/${bijou.id}`);
        if (response.ok) {
          const details = await response.json();
          setBijouTrouve({ ...bijou, details });
        }
      }
    } catch (error) {
      console.error('Erreur chargement détails:', error);
    }
  };

  const ajouterLigne = () => {
    if (!bijouTrouve || quantite < 1) {
      alert('Veuillez sélectionner un bijou et une quantité valide');
      return;
    }

    // Données du modèle à copier dans chaque sous-ligne
    const pierresOriginales = bijouTrouve.details?.pierres || [];
    const composantsOriginaux = (bijouTrouve.details?.composants || []).map(c => ({
      id: c.cout_fabrication_id || c.id,
      nom: c.nom || '?',
      quantite: c.quantite || 1,
      prix_unitaire: c.prix_unitaire || c.cout || 0,
    }));

    const sousLignes = [];
    for (let i = 0; i < quantite; i++) {
      sousLignes.push({
        id: `${Date.now()}-${i}`,
        numero: i + 1,
        couleur_or: bijouTrouve.metal || 'JAUNE',
        pierres: pierresOriginales.map(p => ({
          reference: p.reference || '',
          designation: p.designation || p.nom || '',
          quantite: p.quantite_carats || p.quantite || 1,
        })),
        composants: composantsOriginaux.map(c => ({ ...c })),
        poids_or: bijouTrouve.details?.poids_or || 0,
        notes: '',
      });
    }

    const nouvelleLigne = {
      id: Date.now(),
      bijou_original_id: bijouTrouve.id,
      reference: bijouTrouve.code_article || bijouTrouve.reference_produit,
      numero_ordre: bijouTrouve.numero_ordre,
      type_bijou: bijouTrouve.type_bijou,
      description: bijouTrouve.description,
      quantite: quantite,
      poids_or: bijouTrouve.details?.poids_or || 0,
      _source: bijouTrouve._source || 'of',
      _collection: bijouTrouve._collection,
      _pvp: bijouTrouve._pvp,
      moule_numero: formMoule || '',
      sousLignes: sousLignes
    };

    setLignesCommande([...lignesCommande, nouvelleLigne]);

    // Reset
    setBijouTrouve(null);
    setReferenceSearch('');
    setFormMoule('');
    setQuantite(1);
  };

  const toggleLigne = (ligneId) => {
    setLigneExpanded(ligneExpanded === ligneId ? null : ligneId);
  };

  const supprimerLigne = (ligneId) => {
    setLignesCommande(lignesCommande.filter(l => l.id !== ligneId));
  };

  const modifierSousLigne = (ligneId, sousLigneId, field, value) => {
    const nouvelles = lignesCommande.map(ligne => {
      if (ligne.id === ligneId) {
        return {
          ...ligne,
          sousLignes: ligne.sousLignes.map(sl => 
            sl.id === sousLigneId ? { ...sl, [field]: value } : sl
          )
        };
      }
      return ligne;
    });
    setLignesCommande(nouvelles);
  };

  const ajouterPierre = (ligneId, sousLigneId, pierre) => {
    const nouvelles = lignesCommande.map(ligne => {
      if (ligne.id === ligneId) {
        return {
          ...ligne,
          sousLignes: ligne.sousLignes.map(sl => {
            if (sl.id === sousLigneId) {
              return {
                ...sl,
                pierres: [...(sl.pierres || []), pierre]
              };
            }
            return sl;
          })
        };
      }
      return ligne;
    });
    setLignesCommande(nouvelles);
  };

  const supprimerPierre = (ligneId, sousLigneId, pierreIndex) => {
    const nouvelles = lignesCommande.map(ligne => {
      if (ligne.id === ligneId) {
        return {
          ...ligne,
          sousLignes: ligne.sousLignes.map(sl => {
            if (sl.id === sousLigneId) {
              return {
                ...sl,
                pierres: sl.pierres.filter((_, i) => i !== pierreIndex)
              };
            }
            return sl;
          })
        };
      }
      return ligne;
    });
    setLignesCommande(nouvelles);
  };

  const handlePierreSearch = (value) => {
    setPierreSearch(value);
    if (value.length >= 1) {
      const filtered = pierresDisponibles.filter(p =>
        p.reference?.toLowerCase().includes(value.toLowerCase()) ||
        p.designation?.toLowerCase().includes(value.toLowerCase()) ||
        p.nom?.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredPierres(filtered);
      setShowPierreSuggestions(true);
    } else {
      setFilteredPierres(pierresDisponibles);
      setShowPierreSuggestions(false);
    }
  };

  const lancerProduction = async () => {
    if (lignesCommande.length === 0) {
      alert('Veuillez ajouter au moins un bijou');
      return;
    }

    if (!destinataire) {
      alert('Veuillez sélectionner un client ou une boutique');
      return;
    }

    // Préparer les données pour le backend
    const bijoux = [];
    lignesCommande.forEach(ligne => {
      ligne.sousLignes.forEach(sl => {
        bijoux.push({
          bijou_original_id: ligne.bijou_original_id,
          reference: ligne.reference,
          reference_article: ligne.reference,
          type_bijou: ligne.type_bijou,
          couleur_or: sl.couleur_or,
          pierres: sl.pierres,
          composants: sl.composants || [],
          poids_or: sl.poids_or || ligne.poids_or || 0,
          notes: sl.notes || '',
          moule_numero: ligne.moule_numero || '',
          quantite: 1,
          _source: ligne._source || 'of',
        });
      });
    });

    try {
      const response = await fetch(`${API_URL}/atelier/commande-production`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          boutique_id: destinataire.type === 'boutique' ? destinataire.id : null,
          client_id: destinataire.type === 'client' ? destinataire.id : null,
          client_nom: destinataire.nom || '',  // toujours inclure le nom pour fallback
          bijoux: bijoux
        })
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Commande lancée avec succès !\n${bijoux.length} ordre(s) créé(s)`);
        onLancerProduction();
        onClose();
      } else {
        const error = await response.json();
        alert(`Erreur: ${error.error || 'Impossible de lancer la production'}`);
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors du lancement de la production');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-2xl sm:max-w-6xl w-full max-h-[95vh] sm:max-h-[90vh] flex flex-col">
        {/* En-tête */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center sticky top-0 z-10 rounded-t-2xl sm:rounded-t-lg">
          <h2 className="text-lg sm:text-2xl font-bold">Nouvelle Commande</h2>
          <button onClick={onClose} className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-1.5 sm:p-2">
            <X className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        </div>

        <div className="p-3 sm:p-6 space-y-4 sm:space-y-6 overflow-y-auto flex-1">
          {/* Titre simplifié */}
          <div className="text-center mb-4">
            <h3 className="text-xl font-bold text-gray-800">Nouvelle Commande</h3>
            <p className="text-sm text-gray-500">Sélectionnez un client/boutique puis ajoutez des bijoux au panier</p>
          </div>

          {/* Section Destinataire (Client ou Boutique) */}
          <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4">
            <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
              <User className="h-5 w-5" />
              Pour qui ? (Client ou Boutique)
            </h3>
            <div className="relative">
              <label className="block text-sm font-medium mb-1">Rechercher un client ou une boutique</label>
              <input
                type="text"
                value={destinataireSearch}
                onChange={(e) => handleDestinataireSearch(e.target.value)}
                placeholder="Tapez un nom de client ou de boutique..."
                className="w-full px-3 py-2 border rounded-lg"
              />
              {showDestinataireSuggestions && filteredDestinataires.length > 0 && (
                <div className="absolute z-[100] w-full bottom-full mb-1 bg-white border-2 border-blue-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredDestinataires.map((dest, index) => (
                    <div
                      key={`${dest.type}-${dest.id}`}
                      onClick={() => selectDestinataire(dest)}
                      className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-semibold">{dest.nom}</div>
                          {dest.details && <div className="text-xs text-gray-600">{dest.details}</div>}
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
                          dest.type === 'client' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-purple-100 text-purple-800'
                        }`}>
                          {dest.type === 'client' ? '👤 Client' : '🏪 Boutique'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {destinataire && (
                <div className="mt-2 p-3 bg-white border-2 border-blue-400 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-blue-800">{destinataire.nom}</div>
                      <div className="text-xs text-gray-600">{destinataire.details}</div>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
                      destinataire.type === 'client' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-purple-100 text-purple-800'
                    }`}>
                      {destinataire.type === 'client' ? '👤 Client' : '🏪 Boutique'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Section Ajout Bijou - Visible seulement si destinataire sélectionné */}
          {destinataire && (
            <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4">
              <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                <Package className="h-5 w-5" />
                Ajouter un bijou existant
              </h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2 relative">
                <label className="block text-sm font-medium mb-1">Référence du bijou</label>
                <input
                  type="text"
                  value={referenceSearch}
                  onChange={(e) => handleReferenceSearch(e.target.value)}
                  placeholder="Tapez nom ou référence (min 2 caractères)..."
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                />
                {showBijouSuggestions && filteredBijoux.length > 0 && (
                  <div className="absolute z-[100] w-full bottom-full mb-1 bg-white border-2 border-green-300 rounded-lg shadow-xl max-h-80 overflow-y-auto">
                    <div className="sticky top-0 bg-green-100 px-4 py-2 text-sm font-semibold text-green-800 border-b border-green-200">
                      {filteredBijoux.length} bijou{filteredBijoux.length > 1 ? 'x' : ''} trouvé{filteredBijoux.length > 1 ? 's' : ''}
                    </div>
                    {filteredBijoux.map(b => (
                      <div
                        key={`${b._source}-${b.id}`}
                        onClick={() => selectBijou(b)}
                        className="px-4 py-3 hover:bg-green-50 cursor-pointer border-b last:border-b-0 transition-colors"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-bold text-gray-900">{b.code_article || b.numero_ordre}</span>
                              <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                                b._source === 'catalogue' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                              }`}>{b._source === 'catalogue' ? 'Catalogue' : 'OF'}</span>
                            </div>
                            <div className="text-sm text-gray-600">{b.type_bijou} {b._collection ? `· ${b._collection}` : ''}</div>
                            <div className="text-xs text-gray-500 truncate">{b.description}</div>
                          </div>
                          <div className="text-right ml-3">
                            {b._pvp > 0 && <div className="text-sm font-semibold text-green-600">{parseFloat(b._pvp).toLocaleString('fr-FR')} DH</div>}
                            {b._pieces && <div className="text-xs text-gray-400">{b._pieces.length} pièce(s)</div>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {referenceSearch.length >= 2 && filteredBijoux.length === 0 && (
                  <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-4 text-center text-gray-500 text-sm">
                    Aucun bijou trouvé
                  </div>
                )}
                {bijouTrouve && (
                  <div className="mt-2 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-purple-800">{bijouTrouve.code_article || bijouTrouve.numero_ordre}</span>
                      <span className="text-sm text-gray-600">{bijouTrouve.type_bijou}</span>
                      {bijouTrouve._collection && <span className="text-xs text-indigo-600">· {bijouTrouve._collection}</span>}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{bijouTrouve.description}</div>
                    {bijouTrouve.details && (
                      <div className="flex gap-3 mt-1.5 text-xs">
                        {bijouTrouve.details.poids_or > 0 && <span className="text-amber-700">Or: {bijouTrouve.details.poids_or}g</span>}
                        {bijouTrouve.details.pierres?.length > 0 && <span className="text-indigo-600">{bijouTrouve.details.pierres.length} pierre(s)</span>}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Quantité</label>
                  <input
                    type="text" inputMode="decimal" onFocus={e => e.target.select()}
                    min="1"
                    value={quantite}
                    onChange={(e) => setQuantite(parseInt(e.target.value) || 1)}
                    className="w-full px-3 py-2 border rounded-lg text-lg font-semibold"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">N° Moule (optionnel)</label>
                  <input
                    type="text"
                    value={formMoule || ''}
                    onChange={(e) => setFormMoule(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    placeholder="Ex: 6-136"
                  />
                </div>
              </div>
              <div>
                <button
                  onClick={ajouterLigne}
                  disabled={!bijouTrouve}
                  className={`mt-2 w-full py-2 rounded-lg font-semibold flex items-center justify-center gap-2 ${
                    bijouTrouve
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <Plus size={20} />
                  Ajouter
                </button>
              </div>
            </div>
            </div>
          )}

          {/* Section Devis - Toujours visible */}
          {!destinataire && (
            <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-4">
              <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Devis validés disponibles
              </h3>
              {devisValides.length > 0 ? (
                <div className="grid grid-cols-1 gap-3 max-h-96 overflow-y-auto">
                  {devisValides.map(devis => (
                    <div
                      key={devis.id}
                      onClick={() => {
                        // Ajouter le devis au panier
                        const nouvelleLigne = {
                          id: Date.now(),
                          bijou_original_id: devis.id,
                          reference: devis.numero_devis,
                          numero_ordre: devis.numero_devis,
                          type_bijou: devis.type_bijou,
                          description: devis.description,
                          quantite: 1,
                          sousLignes: [{
                            id: `${Date.now()}-1`,
                            numero: 1,
                            couleur_or: devis.metal || 'JAUNE',
                            pierres: []
                          }]
                        };
                        setLignesCommande([...lignesCommande, nouvelleLigne]);
                        // Auto-remplir le destinataire depuis le client_nom du devis
                        if (devis.client_nom && !destinataire) {
                          setDestinataire({ type: 'texte', id: null, nom: devis.client_nom, details: `Depuis devis ${devis.numero_devis}` });
                          setDestinataireSearch(devis.client_nom);
                        }
                      }}
                      className="p-4 bg-white border-2 border-orange-200 rounded-lg hover:border-orange-400 hover:shadow-md cursor-pointer transition-all"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-bold text-lg text-orange-800">{devis.numero_devis}</div>
                          <div className="text-sm text-gray-600 mt-1">{devis.type_bijou}</div>
                          <div className="text-sm text-gray-500">{devis.description}</div>
                          {devis.client_nom && (
                            <div className="text-xs text-gray-500 mt-1">Client: {devis.client_nom}</div>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-green-600">{devis.prix_total}€</div>
                          <div className="text-xs text-gray-500">{devis.metal}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  <p>Aucun devis validé disponible</p>
                </div>
              )}
            </div>
          )}

          {/* Tableau des lignes */}
          {lignesCommande.length > 0 && (
            <div className="border-2 border-gray-300 rounded-lg overflow-hidden">
              <div className="bg-gray-100 px-4 py-3 font-bold flex justify-between items-center">
                <span>Lignes de commande ({lignesCommande.length})</span>
                <span className="text-sm text-gray-600">
                  Total: {lignesCommande.reduce((sum, l) => sum + l.quantite, 0)} bijou(x)
                </span>
              </div>
              <div className="divide-y">
                {lignesCommande.map((ligne, index) => (
                  <div key={ligne.id}>
                    {/* Ligne principale */}
                    <div className="bg-white hover:bg-gray-50 transition-colors">
                      <div className="flex items-center px-4 py-3 gap-3">
                        <button
                          onClick={() => toggleLigne(ligne.id)}
                          className="text-gray-600 hover:text-gray-800"
                        >
                          {ligneExpanded === ligne.id ? (
                            <ChevronDown size={20} />
                          ) : (
                            <ChevronRight size={20} />
                          )}
                        </button>
                        <div className="flex-1 grid grid-cols-5 gap-4">
                          <div className="font-semibold">{index + 1}</div>
                          <div className="col-span-2">
                            <div className="font-semibold">{ligne.reference}</div>
                            <div className="text-sm text-gray-600">{ligne.type_bijou}</div>
                            {ligne.moule_numero && <div className="text-xs text-amber-600">Moule: {ligne.moule_numero}</div>}
                          </div>
                          <div className="text-center">
                            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-semibold">
                              Qté: {ligne.quantite}
                            </span>
                            {ligne.poids_or > 0 && (
                              <div className="text-xs text-gray-500 mt-1">
                                Or: {(parseFloat(ligne.poids_or) * ligne.quantite).toFixed(2)}g total
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <button
                              onClick={() => supprimerLigne(ligne.id)}
                              className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Sous-lignes (détails par pièce) */}
                    {ligneExpanded === ligne.id && (
                      <div className="bg-gray-50 px-4 py-3 border-t">
                        <div className="space-y-4">
                          {ligne.sousLignes.map((sl) => (
                            <div key={sl.id} className="bg-white border rounded-xl p-5 shadow-sm">
                              {/* Header pièce */}
                              <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                  <span className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center font-bold text-sm">
                                    {sl.numero}
                                  </span>
                                  <span className="font-semibold text-gray-800">Piece #{sl.numero}</span>
                                </div>
                                {/* Couleur or */}
                                <div className="flex gap-1.5">
                                  {['JAUNE', 'BLANC', 'ROSE'].map(couleur => (
                                    <button key={couleur}
                                      onClick={() => modifierSousLigne(ligne.id, sl.id, 'couleur_or', couleur)}
                                      className={`flex items-center gap-1 px-2.5 py-1 rounded-lg border-2 text-xs font-medium transition-all ${
                                        sl.couleur_or === couleur
                                          ? couleur === 'JAUNE' ? 'bg-yellow-100 border-yellow-500 text-yellow-800'
                                          : couleur === 'BLANC' ? 'bg-gray-100 border-gray-500 text-gray-800'
                                          : 'bg-pink-100 border-pink-500 text-pink-800'
                                          : 'border-gray-200 text-gray-500'
                                      }`}>
                                      <span className={`w-3 h-3 rounded-full ${couleur === 'JAUNE' ? 'bg-yellow-400' : couleur === 'BLANC' ? 'bg-gray-300' : 'bg-pink-300'}`} />
                                      {couleur}
                                    </button>
                                  ))}
                                </div>
                              </div>

                              {/* Or / Poids */}
                              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-semibold text-amber-800">Or {sl.couleur_or} 18k</span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-500">Poids :</span>
                                    <input type="text" value={sl.poids_or || ''}
                                      onChange={e => modifierSousLigne(ligne.id, sl.id, 'poids_or', e.target.value.replace(',', '.'))}
                                      className="w-20 px-2 py-1 border border-amber-300 rounded text-sm text-center font-semibold" placeholder="g" />
                                    <span className="text-xs text-amber-600">g</span>
                                  </div>
                                </div>
                              </div>

                              {/* Composants fabrication */}
                              {sl.composants && sl.composants.length > 0 && (
                                <div className="mb-3">
                                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Composants</div>
                                  <div className="space-y-1">
                                    {sl.composants.map((comp, ci) => (
                                      <div key={ci} className="flex items-center gap-2 bg-gray-50 rounded px-3 py-1.5">
                                        <span className="flex-1 text-sm">{comp.nom}</span>
                                        <input type="text" value={comp.quantite}
                                          onChange={e => {
                                            const newComps = [...sl.composants];
                                            newComps[ci] = { ...newComps[ci], quantite: e.target.value.replace(',', '.') };
                                            modifierSousLigne(ligne.id, sl.id, 'composants', newComps);
                                          }}
                                          className="w-14 px-2 py-0.5 border rounded text-xs text-center" />
                                        <button onClick={() => {
                                          modifierSousLigne(ligne.id, sl.id, 'composants', sl.composants.filter((_, i) => i !== ci));
                                        }} className="text-red-400 hover:text-red-600"><Trash2 size={12} /></button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                                <div className="space-y-1 mb-2">
                                  {(sl.pierres && sl.pierres.length > 0) ? (
                                    sl.pierres.map((p, i) => {
                                      const nbP = parseInt(p.nb_pierres) || 1;
                                      const pdsPce = parseFloat(p.poids_par_pierre) || 0;
                                      const totalCt = pdsPce > 0 ? (nbP * pdsPce) : parseFloat(p.quantite) || 0;
                                      const stockInsuf = p.stock_disponible_ct && totalCt > p.stock_disponible_ct;
                                      return (
                                      <div key={i} className={`bg-indigo-50 border rounded px-3 py-2 ${stockInsuf ? 'border-red-400' : 'border-indigo-200'}`}>
                                        <div className="flex items-center gap-2">
                                          <span className="flex-1 text-sm font-medium text-indigo-800">{p.reference} - {p.designation || 'Pierre'}</span>
                                          <button onClick={() => supprimerPierre(ligne.id, sl.id, i)}
                                            className="text-red-400 hover:text-red-600"><Trash2 size={12} /></button>
                                        </div>
                                        <div className="flex items-center gap-3 mt-1.5">
                                          <div className="flex items-center gap-1">
                                            <label className="text-xs text-gray-500">Nb pierres:</label>
                                            <input type="text" inputMode="decimal" onFocus={e => e.target.select()} min="1" value={nbP}
                                              onChange={e => {
                                                const newP = [...sl.pierres];
                                                const newNb = parseInt(e.target.value) || 1;
                                                const newTotal = pdsPce > 0 ? newNb * pdsPce : newNb;
                                                newP[i] = { ...newP[i], nb_pierres: newNb, quantite: newTotal };
                                                modifierSousLigne(ligne.id, sl.id, 'pierres', newP);
                                              }}
                                              className="w-14 px-2 py-0.5 border rounded text-xs text-center" />
                                          </div>
                                          {pdsPce > 0 && (
                                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded font-semibold">
                                              {pdsPce.toFixed(4)} ct/pce = {totalCt.toFixed(4)} ct total
                                            </span>
                                          )}
                                          {p.nb_pierres_dispo > 0 && (
                                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                              {p.nb_pierres_dispo} dispo
                                            </span>
                                          )}
                                          {stockInsuf && (
                                            <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded font-semibold">Stock insuf.</span>
                                          )}
                                        </div>
                                      </div>
                                    );})
                                  ) : (
                                    <span className="text-gray-400 text-xs">Aucune pierre</span>
                                  )}
                                </div>
                                {/* Ajouter pierre */}
                                <div className="relative">
                                  <input type="text" value={activePierreSl === sl.id ? pierreSearch : ''}
                                    onChange={e => { setActivePierreSl(sl.id); setPierreSearch(e.target.value); handlePierreSearch(e.target.value); }}
                                    onFocus={() => { setActivePierreSl(sl.id); setFilteredPierres(pierresDisponibles); setShowPierreSuggestions(true); }}
                                    onBlur={() => setTimeout(() => { if (activePierreSl === sl.id) setShowPierreSuggestions(false); }, 200)}
                                    placeholder="Ajouter une pierre..."
                                    className="w-full px-3 py-1.5 border border-dashed border-indigo-300 rounded text-xs focus:ring-1 focus:ring-indigo-400" />
                                  {showPierreSuggestions && activePierreSl === sl.id && filteredPierres.length > 0 && (
                                    <div className="absolute z-50 w-full mt-1 bg-white border border-indigo-300 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                                      {filteredPierres.slice(0, 10).map((pierre, idx) => (
                                        <div key={idx}
                                          onMouseDown={() => {
                                            const desig = (pierre.designation || '') + ' ' + (pierre.reference || '');
                                            const matchCt = desig.match(/(\d+[.,]\d+)\s*ct/i);
                                            const poidsParPierre = matchCt ? parseFloat(matchCt[1].replace(',', '.')) : 0;
                                            const stockCt = parseFloat(pierre.quantite_disponible || 0);
                                            const nbDispo = poidsParPierre > 0 ? Math.floor(stockCt / poidsParPierre) : 0;
                                            ajouterPierre(ligne.id, sl.id, {
                                              reference: pierre.reference,
                                              designation: pierre.designation || pierre.nom,
                                              nb_pierres: 1,
                                              poids_par_pierre: poidsParPierre,
                                              quantite: poidsParPierre || 1,
                                              stock_disponible_ct: stockCt,
                                              nb_pierres_dispo: nbDispo,
                                              prix_achat_carat: pierre.prix_achat_carat || pierre.prix_achat_unitaire || 0
                                            });
                                            setPierreSearch(''); setShowPierreSuggestions(false); setActivePierreSl(null);
                                          }}
                                          className="px-3 py-2 hover:bg-indigo-50 cursor-pointer border-b last:border-0 text-sm">
                                          <span className="font-medium text-indigo-800">{pierre.reference}</span>
                                          <span className="text-gray-500 ml-2">{pierre.designation || ''}</span>
                                          <div className="flex gap-2 mt-0.5">
                                            <span className="text-xs bg-purple-50 text-purple-700 px-1.5 rounded">{parseFloat(pierre.quantite_disponible || 0).toFixed(4)} ct dispo</span>
                                            {pierre.prix_achat_carat > 0 && <span className="text-xs bg-green-50 text-green-700 px-1.5 rounded">{parseFloat(pierre.prix_achat_carat).toFixed(0)} DH/ct</span>}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>

                              {/* Notes */}
                              <div>
                                <input type="text" value={sl.notes || ''} placeholder="Notes / particularites (taille doigt, dimensions...)"
                                  onChange={e => modifierSousLigne(ligne.id, sl.id, 'notes', e.target.value)}
                                  className="w-full px-3 py-1.5 border border-gray-200 rounded text-xs text-gray-600" />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bouton Lancer Production */}
          {lignesCommande.length > 0 && (
            <button
              onClick={lancerProduction}
              className="w-full py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 font-bold text-lg shadow-lg"
            >
              🚀 Lancer la Production ({lignesCommande.reduce((sum, l) => sum + l.quantite, 0)} bijou(x))
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommandeProductionClient;
